/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  AnagraficaDelegatoDTO,
  CreaAnagraficaDelegatoRequest,
  GetFrontAnagraficheDelegatiParams,
  WsRispostaPaginataListAnagraficaDelegatoDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Recupera il dettaglio dell'anagrafica di un delegato associata all'utente corrente
 * @summary Dettaglio dell'anagrafica delegato associata all'utente corrente
 */
export const getFrontAnagraficheDelegatiId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<AnagraficaDelegatoDTO>(
    {
      url: `/framework-cmp-domande/anagrafiche-delegati/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontAnagraficheDelegatiIdQueryKey = (id: string) =>
  [`/framework-cmp-domande/anagrafiche-delegati/${id}`] as const;

export const getGetFrontAnagraficheDelegatiIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontAnagraficheDelegatiId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAnagraficheDelegatiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontAnagraficheDelegatiId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontAnagraficheDelegatiIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontAnagraficheDelegatiId>>
  > = ({ signal }) => getFrontAnagraficheDelegatiId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontAnagraficheDelegatiIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontAnagraficheDelegatiId>>
>;
export type GetFrontAnagraficheDelegatiIdQueryError = ErrorType<unknown>;

/**
 * @summary Dettaglio dell'anagrafica delegato associata all'utente corrente
 */
export const useGetFrontAnagraficheDelegatiId = <
  TData = Awaited<ReturnType<typeof getFrontAnagraficheDelegatiId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAnagraficheDelegatiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontAnagraficheDelegatiIdQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Modifica una anagrafica delegato associata all'utente corrente
 * @summary Modifica una anagrafica delegato associata all'utente corrente
 */
export const putFrontAnagraficheDelegatiId = (
  id: string,
  creaAnagraficaDelegatoRequest: CreaAnagraficaDelegatoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<AnagraficaDelegatoDTO>(
    {
      url: `/framework-cmp-domande/anagrafiche-delegati/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: creaAnagraficaDelegatoRequest,
    },
    options,
  );
};

export const getPutFrontAnagraficheDelegatiIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontAnagraficheDelegatiId>>,
    TError,
    { id: string; data: CreaAnagraficaDelegatoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putFrontAnagraficheDelegatiId>>,
  TError,
  { id: string; data: CreaAnagraficaDelegatoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putFrontAnagraficheDelegatiId>>,
    { id: string; data: CreaAnagraficaDelegatoRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putFrontAnagraficheDelegatiId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutFrontAnagraficheDelegatiIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putFrontAnagraficheDelegatiId>>
>;
export type PutFrontAnagraficheDelegatiIdMutationBody =
  CreaAnagraficaDelegatoRequest;
export type PutFrontAnagraficheDelegatiIdMutationError = ErrorType<unknown>;

/**
 * @summary Modifica una anagrafica delegato associata all'utente corrente
 */
export const usePutFrontAnagraficheDelegatiId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontAnagraficheDelegatiId>>,
    TError,
    { id: string; data: CreaAnagraficaDelegatoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutFrontAnagraficheDelegatiIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cancellazione dell'anagrafica delegato associata all'utente corrente
 * @summary Eliminazione anagrafica delegato associata all'utente corrente
 */
export const deleteFrontAnagraficheDelegatiId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cmp-domande/anagrafiche-delegati/${id}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteFrontAnagraficheDelegatiIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFrontAnagraficheDelegatiId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteFrontAnagraficheDelegatiId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteFrontAnagraficheDelegatiId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteFrontAnagraficheDelegatiId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteFrontAnagraficheDelegatiIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteFrontAnagraficheDelegatiId>>
>;

export type DeleteFrontAnagraficheDelegatiIdMutationError = ErrorType<unknown>;

/**
 * @summary Eliminazione anagrafica delegato associata all'utente corrente
 */
export const useDeleteFrontAnagraficheDelegatiId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFrontAnagraficheDelegatiId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteFrontAnagraficheDelegatiIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cerca una serie di anagrafiche delegati associate all'utente corrente
 * @summary Cerca una serie di anagrafiche delegati associate all'utente corrente
 */
export const getFrontAnagraficheDelegati = (
  params?: GetFrontAnagraficheDelegatiParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListAnagraficaDelegatoDTO>(
    {
      url: `/framework-cmp-domande/anagrafiche-delegati`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontAnagraficheDelegatiQueryKey = (
  params?: GetFrontAnagraficheDelegatiParams,
) =>
  [
    `/framework-cmp-domande/anagrafiche-delegati`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontAnagraficheDelegatiQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontAnagraficheDelegati>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontAnagraficheDelegatiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAnagraficheDelegati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontAnagraficheDelegati>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontAnagraficheDelegatiQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontAnagraficheDelegati>>
  > = ({ signal }) =>
    getFrontAnagraficheDelegati(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontAnagraficheDelegatiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontAnagraficheDelegati>>
>;
export type GetFrontAnagraficheDelegatiQueryError = ErrorType<unknown>;

/**
 * @summary Cerca una serie di anagrafiche delegati associate all'utente corrente
 */
export const useGetFrontAnagraficheDelegati = <
  TData = Awaited<ReturnType<typeof getFrontAnagraficheDelegati>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontAnagraficheDelegatiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAnagraficheDelegati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontAnagraficheDelegatiQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea una anagrafica delegato associandola alla rubrica dell'utente corrente
 * @summary Crea una anagrafica delegato associandola alla rubrica dell'utente corrente
 */
export const postFrontAnagraficheDelegati = (
  creaAnagraficaDelegatoRequest: CreaAnagraficaDelegatoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<AnagraficaDelegatoDTO>(
    {
      url: `/framework-cmp-domande/anagrafiche-delegati`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: creaAnagraficaDelegatoRequest,
    },
    options,
  );
};

export const getPostFrontAnagraficheDelegatiMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontAnagraficheDelegati>>,
    TError,
    { data: CreaAnagraficaDelegatoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontAnagraficheDelegati>>,
  TError,
  { data: CreaAnagraficaDelegatoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontAnagraficheDelegati>>,
    { data: CreaAnagraficaDelegatoRequest }
  > = props => {
    const { data } = props ?? {};

    return postFrontAnagraficheDelegati(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontAnagraficheDelegatiMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontAnagraficheDelegati>>
>;
export type PostFrontAnagraficheDelegatiMutationBody =
  CreaAnagraficaDelegatoRequest;
export type PostFrontAnagraficheDelegatiMutationError = ErrorType<unknown>;

/**
 * @summary Crea una anagrafica delegato associandola alla rubrica dell'utente corrente
 */
export const usePostFrontAnagraficheDelegati = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontAnagraficheDelegati>>,
    TError,
    { data: CreaAnagraficaDelegatoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontAnagraficheDelegatiMutationOptions(options);

  return useMutation(mutationOptions);
};
